import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, forkJoin, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ClosableModalWithErrorResponseModel } from '../../../shared/components/base/closable-modal-with-error-response-model';
import { DropdownsModalComponent } from '../../../shared/components/dropdowns-modal/dropdowns-modal.component';

import { NotificationService, FunctionalAnalysesService, TransactionsService } from '../../../shared/services';
import { ModalService } from '../../../shared/modules/modal';

import { ListResponse, ResultResponse } from '../../../shared/interfaces';
import {
  ModifyFunctionalAnalysisTemplateModalPayload, TbaFunctionalAnalysisGlobalTemplate, DropdownsModalDropdownData, DropdownsModalPayload,
  TransactionType, ImportFunctionalAnalysisGlobalTemplatePayload, GetAllTransactionTypesResponse, FunctionalAnalysisGlobalTemplate
} from '../../../shared/models';
import { ConfigurationConstants } from '../../../shared/constants/config';

@Component({
  template: ''
})
export class ModifyUncontrolledFunctionalAnalysisTemplateModalComponent extends ClosableModalWithErrorResponseModel<void> implements OnInit, OnDestroy {

  data: ModifyFunctionalAnalysisTemplateModalPayload;

  private transactionTypes: TransactionType[];
  private allGlobalTemplates: TbaFunctionalAnalysisGlobalTemplate[];
  private selectedGlobalTemplateId: number;

  private transactionTypesDropdown: DropdownsModalDropdownData;
  private allGlobalTemplatesDropdown: DropdownsModalDropdownData;
  private wasTemplateModifySuccessful$: Subject<boolean> = new Subject<boolean>();

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _transactionService: TransactionsService,
    private _functionalAnalysesService: FunctionalAnalysesService
  ) {
    super(notificationService, modalService);
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.getAllModalData().subscribe(() => {
        this.initializeModalDropdowns();
        (this.data.isDeleteModal) ? this.openGenericDeleteModal() : this.openGenericImportModal();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private getAllModalData(): Observable<[ResultResponse<GetAllTransactionTypesResponse>, ListResponse<FunctionalAnalysisGlobalTemplate>]> {
    return forkJoin(
      this.getTransactionTypes(),
      this.getAllGlobalTemplates()
    );
  }

  private getTransactionTypes(): Observable<ResultResponse<GetAllTransactionTypesResponse>> {
    return this._transactionService.getAllTransactionTypes().pipe(
      tap(({ result }: ResultResponse<GetAllTransactionTypesResponse>) => {
        this.transactionTypes = result.transactionTypes;
      })
    );
  }

  private getAllGlobalTemplates(): Observable<ListResponse<FunctionalAnalysisGlobalTemplate>> {
    return this._functionalAnalysesService.getAllGlobalTemplates(this.data.analysisType).pipe(
      tap(({ data: globalTemplates }: ListResponse<TbaFunctionalAnalysisGlobalTemplate>) => {
        this.allGlobalTemplates = globalTemplates;
      })
    );
  }

  private initializeModalDropdowns(): void {
    this.transactionTypesDropdown = {
      name: 'transaction.transaction_type',
      elements: this.transactionTypes,
      keyForDisplay: 'name',
      onSelected: this.onTransactionTypeChange.bind(this)
    };

    this.allGlobalTemplatesDropdown = {
      name: 'functional_analysis.template',
      elements: this.allGlobalTemplates,
      keyForDisplay: 'templateName',
      onSelected: this.onGlobalTemplateChange.bind(this)
    };
  }

  private onTransactionTypeChange(transactionType: TransactionType): void {
    this.allGlobalTemplatesDropdown.elements = this.allGlobalTemplates.filter((template) => {
      return template.transactionTypeId === transactionType.transactionTypeId;
    });
  }

  private onGlobalTemplateChange(globalTemplate: TbaFunctionalAnalysisGlobalTemplate): void {
    this.selectedGlobalTemplateId = globalTemplate.functionalAnalysisGlobalTemplateId;
  }

  private openGenericDeleteModal(): void {
    this.modalService.openModal({
      component: DropdownsModalComponent,
      data: {
        modalTitle: 'functional_analysis.delete_functional_analysis_template',
        confirmationText: 'functional_analysis.delete_confirmation_text',
        targetConfirmationText: ConfigurationConstants.CONFIRMATION_TEXT,
        dropdowns: [
          this.transactionTypesDropdown,
          this.allGlobalTemplatesDropdown
        ],
        saveClicked: this.deleteTemplate.bind(this),
        saved: this.wasTemplateModifySuccessful$
      } as DropdownsModalPayload,
      success: this.success,
      cancel: () => this.wasTemplateModifySuccessful$.next(false),
    });
  }

  private deleteTemplate(): void {
    this.subscriptions.push(
      this._functionalAnalysesService.deleteGlobalTemplate(this.data.analysisType, this.selectedGlobalTemplateId).subscribe({
        next: () => this.wasTemplateModifySuccessful$.next(true),
        error: () => this.wasTemplateModifySuccessful$.next(false)
      })
    );
  }

  private openGenericImportModal(): void {
    this.modalService.openModal({
      component: DropdownsModalComponent,
      data: {
        modalTitle: 'functional_analysis.import_functional_analysis_template',
        dropdowns: [
          this.transactionTypesDropdown,
          this.allGlobalTemplatesDropdown
        ],
        saveClicked: this.importTemplate.bind(this),
        saved: this.wasTemplateModifySuccessful$
      } as DropdownsModalPayload,
      success: this.success,
      cancel: () => this.wasTemplateModifySuccessful$.next(false),
    });
  }

  private importTemplate(): void {
    const payload: ImportFunctionalAnalysisGlobalTemplatePayload = { templateId: this.selectedGlobalTemplateId };

    this.subscriptions.push(
      this._functionalAnalysesService.importGlobalTemplate(this.data.analysisType, this.data.resourceId, payload).subscribe({
        next: () => this.wasTemplateModifySuccessful$.next(true),
        error: () => this.wasTemplateModifySuccessful$.next(false)
      })
    );
  }

}
